import * as React from 'react';

import devNote from '@xing-com/dev-note';

import { AddTag } from './add-tag';
import { DefaultTag } from './default-tag';
import { DeleteTag } from './delete-tag';
import { InteractiveTag } from './interactive-tag';
import { LinkTag } from './link-tag';
import { PlaceholderTag } from './placeholder-tag';
import type { TagProps } from './tag.types';

export const Tag = React.forwardRef<HTMLDivElement, TagProps>(
  (
    {
      badgeProps,
      children,
      className,
      disabled,
      fontWeight,
      href,
      hideBorder,
      leadingIcon,
      maxWidth = '330px',
      onClick,
      onDelete,
      profile,
      rel,
      selected,
      showMiniBadge,
      size = 'medium',
      slotContent,
      target,
      trailingIcon,
      value,
      variant,
      ...props
    },
    forwardedRef
  ) => {
    // TODO: Remove variant property
    if (variant === 'placeholder') {
      devNote.deprecate(
        "The 'placeholder' variant is deprecated. Please use the PlaceholderTag component instead."
      );
    }

    if (variant === 'add') {
      devNote.deprecate(
        "The 'add' variant is deprecated. Please use the AddTag component instead."
      );
    }

    if (variant === 'delete') {
      devNote.deprecate(
        "The 'delete' variant is deprecated. Please use the DeleteTag component instead."
      );
    }

    const isLink = Boolean(href);
    const isInteractive = Boolean(onClick);

    const commonProps = {
      children,
      className,
      fontWeight,
      maxWidth,
      profile,
      ref: forwardedRef,
      size,
    };

    const borderProps = {
      hideBorder,
    };

    const miniBadgeProps = {
      showMiniBadge,
    };

    const slotProps = {
      slotContent,
    };

    const iconProps = {
      leadingIcon,
      trailingIcon,
    };

    const interactiveProps = {
      disabled,
      onClick,
      selected,
      value,
    };

    const linkProps = {
      href,
      onClick,
      rel,
      target,
    };

    return (
      <>
        {variant === 'placeholder' ? (
          <PlaceholderTag
            {...commonProps}
            {...iconProps}
            {...miniBadgeProps}
            {...slotProps}
            {...props}
          />
        ) : variant === 'add' ? (
          <AddTag {...commonProps} {...interactiveProps} {...props} />
        ) : variant === 'delete' ? (
          <DeleteTag
            onDelete={onDelete}
            {...commonProps}
            {...interactiveProps}
            {...props}
          />
        ) : isLink ? (
          <LinkTag
            badgeProps={badgeProps}
            {...commonProps}
            {...borderProps}
            {...iconProps}
            {...miniBadgeProps}
            {...slotProps}
            disabled={disabled}
            selected={selected}
            {...linkProps}
            {...props}
            ref={forwardedRef as React.ForwardedRef<HTMLAnchorElement>}
          />
        ) : isInteractive ? (
          <InteractiveTag
            badgeProps={badgeProps}
            {...commonProps}
            {...borderProps}
            {...iconProps}
            {...miniBadgeProps}
            {...slotProps}
            {...interactiveProps}
            {...props}
          />
        ) : (
          <DefaultTag
            {...commonProps}
            {...borderProps}
            {...miniBadgeProps}
            {...slotProps}
            {...iconProps}
            {...props}
          />
        )}
      </>
    );
  }
);

Tag.displayName = 'Tag';
